/**
 * CanvasEditor Component
 *
 * Full-Mockup Canvas implementation following Printify/Gelato architecture.
 * Canvas size = Mockup size, with Background + Mockup + Designs as Fabric.js layers.
 * Heathered/Texture overlays rendered as HTML elements for CSS mix-blend-mode support.
 *
 * Key Architecture:
 * - Canvas is created at NATIVE mockup dimensions
 * - Wrapper is scaled using CSS transform for responsive display
 * - Fabric.js coordinates always work in native space
 *
 * Layer Stack:
 * - Container: CSS mask-image (cutout)
 * - z-index 4: Texture Overlay (HTML img, mix-blend-mode)
 * - z-index 3: Heathered Overlay (HTML img, mix-blend-mode)
 * - z-index 2: Mockup Overlay (HTML img) — phone case frame above designs
 * - z-index 1: Fabric.js Canvas
 *   - __background__: Background color (Rect, locked)
 *   - __mockup__: Mockup base image (FabricImage, locked) — used for export
 *   - User designs: (selectable, clipPath = printArea)
 *   - __printAreaBorder__: Dashed border (Rect, excludeFromExport)
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  ActiveSelection,
  Canvas as FabricCanvas,
  FabricImage,
  FabricObject,
  Rect,
  Point,
} from 'fabric';
import { Plus, Upload, AlertTriangle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  cutoutMaskStyles,
  isMaskPending,
  useAlphaMask,
  maskBoundsOf,
} from '@/lib/alphaMask';
import { fitToProduct as computeProductFit } from './fitToProduct';
import { getThemeColorVar } from '@/lib/theme';
import {
  CANVAS_TOUCH_SETTINGS,
  CANVAS_SIZING,
  MAX_FILE_SIZE,
  ALLOWED_IMAGE_TYPES,
} from '@/config/constants';
import { SYSTEM_LAYER_NAMES, isSystemLayer } from '@/types/canvas';
import type { CanvasConfig, PrintArea } from '@/types/canvas';
import {
  mapBlendMode,
  getHeatheredOverlayUrl,
  DEFAULT_OVERLAY_SETTINGS,
} from '@/utils/mockupLayers';
import { logger, cropImageToVisibleBounds, setAssetProperties } from '@/utils';
import { useDesignStore, useUIStore } from '@/stores';
import {
  useCanvasStateManager,
  useIsSwitchingPosition,
} from '@/stores/canvasStateManager';
import { useUploadStore } from '@/stores/uploadStore';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import {
  removeSystemLayers,
  removeSystemLayer,
  applyClipPathToUserObjects,
  getUserObjects,
  computeCenterSnap,
  SNAP_THRESHOLD_PX,
  TOUCH_SNAP_SCREEN_PX,
  showGuides,
  hideGuides,
} from '@/services/canvas';
import { getCanvasPrintAreaBounds } from '@/utils/printAreaBounds';
import { isDarkColor } from '@/lib/color-contrast';
import { createGenerationGuard } from '@/utils/generationGuard';
import { uploadAsset } from '@/api/assets';
import { uploadEmbroideryImage } from '@/api/embroidery';
import { EmbroideryThreadModal } from './EmbroideryThreadModal';
import { useEmbroideryMapping } from '@/hooks/useEmbroideryMapping';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import {
  applyCustomControls,
  type ControlLayout,
  type CustomControlOptions,
} from './controls';
import { resolveContainerSize } from './containerSize';
import { deriveCanvasLayerPlacement } from '@/lib/canvasLayerPlacement';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL } from '@designdeck/shared';

// =============================================================================
// CONSTANTS
// =============================================================================

// SVG is part of ALLOWED_IMAGE_TYPES since the API accepts and rasterises
// it; the editor no longer needs its own extra entry.
const ALLOWED_TYPES: readonly string[] = [...ALLOWED_IMAGE_TYPES];

// =============================================================================
// TYPES
// =============================================================================

export interface CanvasEditorProps {
  /** Canvas configuration from template */
  config: CanvasConfig;

  /** Called when canvas is initialized */
  onCanvasReady?: (canvas: FabricCanvas) => void;

  /** Called when canvas content changes */
  onCanvasChange?: () => void;

  /** Additional class names */
  className?: string;

  /**
   * Breathing room around the canvas inside its container.
   *
   * Defaults to the shell's own spacing. A caller that frames the print area
   * itself — the guided entry — passes 0, so the dashed border lands on the
   * frame instead of floating inside it.
   */
  containerPadding?: number;

  /**
   * On-screen diameter of the frame handles, in CSS pixels.
   *
   * Fabric paints the handles onto the canvas, and the canvas is displayed
   * through a CSS scale — so by default the handles shrink and grow with it.
   * A small frame like the guided entry's passes the diameter it wants to
   * see, and the editor converts it with the current display factor.
   */
  controlScreenSize?: number;
  /** Arrangement of the frame handles; "touch" is for phones. */
  controlLayout?: ControlLayout;
  /**
   * Zoom in on the product itself rather than on the whole mockup image:
   * many templates leave a wide margin around the garment, and on a phone
   * that margin is screen the product could use. The product's box comes
   * from its cutout mask; a template without one is shown as is. Never
   * more than FIT_TO_PRODUCT_MAX_ZOOM (fitToProduct.ts), never smaller than
   * the plain fit.
   */
  fitToProduct?: boolean;
  /**
   * Upper bound for the display factor of the mockup. Defaults to the
   * editor's own cap (CANVAS_SIZING.MAX_SCALE_FACTOR); a caller with a zoom
   * control of its own — the Direct Sale desktop — raises it so its zoom is
   * not silently clipped by the fit.
   */
  maxScaleFactor?: number;
  /**
   * Lower bound for the display factor. Defaults to the editor's own floor
   * (CANVAS_SIZING.MIN_SCALE_FACTOR); the guided placement frame lowers it so
   * a smaller frame still shows the whole canvas instead of a clipped part.
   */
  minScaleFactor?: number;
}

/** Whether a drag carries files — a dragged image or link carries none. */
function dragCarriesFiles(e: React.DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes('Files');
}

// =============================================================================
// COMPONENT
// =============================================================================

export function CanvasEditor({
  config,
  onCanvasReady,
  onCanvasChange,
  className,
  containerPadding,
  controlScreenSize,
  controlLayout,
  fitToProduct = false,
  maxScaleFactor = CANVAS_SIZING.MAX_SCALE_FACTOR,
  minScaleFactor = CANVAS_SIZING.MIN_SCALE_FACTOR,
}: CanvasEditorProps) {
  const { t } = useTranslation();
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<FabricCanvas | null>(null);
  const printAreaClipRef = useRef<Rect | null>(null);
  // The print area the editor last acted on, in canvas coordinates; the
  // config-change effect compares against it so a re-render with an equal
  // area does not start another position switch.
  const previousPrintAreaRef = useRef<PrintArea | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previousConfigRef = useRef<CanvasConfig>(config);
  const layerGuardRef = useRef(createGenerationGuard());
  const isInitializedRef = useRef(false);
  const outOfBoundsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPanningRef = useRef(false);
  const lastPanPosRef = useRef({ x: 0, y: 0 });

  // State
  const [isInitialized, setIsInitialized] = useState(false);
  const [mockupLoaded, setMockupLoaded] = useState(false);
  /** The HTML base overlay (shading above the design); drives mockupLoaded */
  const baseImageRef = useRef<HTMLImageElement>(null);
  const [hasDesign, setHasDesign] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isOutOfBounds, setIsOutOfBounds] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Stores
  const toolMode = useUIStore((s) => s.toolMode);
  const { setCanvas, setSelectedObjects, updateSizeInfo, clearSizeInfo } =
    useDesignStore();
  const setHasUnsavedChanges = useDesignStore((s) => s.setHasUnsavedChanges);
  // Subscribed on purpose, read by two effects below: the position-switch
  // effect retries a switch the store refused while the lock was held, and
  // the finalize effect reacts to the lock being released after a design load.
  const isSwitchingPosition = useIsSwitchingPosition();
  const {
    setCanvas: setCanvasInManager,
    setInitialized,
    switchPosition,
    saveToHistory,
    loadSavedPosition,
    updatePreview,
  } = useCanvasStateManager();
  const { activePrintContext, activePosition, activePositionId } =
    useProductSelectionStore();
  const { startUpload, completeUpload, failUpload } = useUploadStore();
  const isEmbroideryProduct = useEmbroideryStore((s) => s.isEmbroideryProduct);
  const isEmbroideryActive = useEmbroideryStore((s) => s.isEmbroideryActive);
  const isEmbroideryProcessing = useEmbroideryStore((s) => s.isProcessing);
  const setEmbroideryActive = useEmbroideryStore((s) => s.setEmbroideryActive);
  const setEmbroideryProcessing = useEmbroideryStore((s) => s.setProcessing);
  const startEmbroideryMapping = useEmbroideryStore((s) => s.startMapping);

  // Extract config values
  const {
    mockupWidth,
    mockupHeight,
    printArea: printAreaConfig,
    mockupImageUrl,
    baseOpacity = 1,
    layerOrder,
    backgroundColor,
    heatheredOverlayUrl,
    textureOverlayUrl,
    shadowOverlayUrl,
    topOverlayUrl,
    cutoutMaskUrl,
    toplayerMaskUrl,
    heatheredBlendMode = DEFAULT_OVERLAY_SETTINGS.heatheredBlendMode,
    heatheredOpacity = DEFAULT_OVERLAY_SETTINGS.heatheredOpacity,
    textureBlendMode = DEFAULT_OVERLAY_SETTINGS.textureBlendMode,
    textureOpacity = DEFAULT_OVERLAY_SETTINGS.textureOpacity,
    shadowBlendMode = DEFAULT_OVERLAY_SETTINGS.shadowBlendMode,
    shadowOpacity = DEFAULT_OVERLAY_SETTINGS.shadowOpacity,
    isHeathered = false,
    printAreaBorder: drawPrintAreaBorder = true,
  } = config;
  // Garment fill behind the mockup cutout — must follow the selected product color
  const effectiveBackgroundColor = backgroundColor || '#FFFFFF';

  // Heathered overlay URL with Shirtigo fallback (only for heathered colors)
  const resolvedHeatheredUrl = getHeatheredOverlayUrl(
    heatheredOverlayUrl,
    isHeathered
  );

  // ─── Layer stack (Shirtigo Mockup Factory model) ─────────────────────────
  // Where each layer lives relative to the Fabric canvas — see
  // deriveCanvasLayerPlacement for the rules (Shirtigo model, embroidery).
  const {
    layersAboveDesign,
    shadingInCanvas,
    overlaysBelowCanvas,
    canvasBackgroundFill,
  } = useMemo(
    () =>
      deriveCanvasLayerPlacement({
        layerOrder,
        shadingBelowDesign: isEmbroideryProduct,
        hasHeathered: !!resolvedHeatheredUrl,
        hasTexture: !!textureOverlayUrl,
        cutoutMaskUrl,
        backgroundColor: effectiveBackgroundColor,
      }),
    [
      layerOrder,
      isEmbroideryProduct,
      resolvedHeatheredUrl,
      textureOverlayUrl,
      cutoutMaskUrl,
      effectiveBackgroundColor,
    ]
  );

  // Where the shading lives decides how initializeLayers learns that the
  // mockup is ready: from Fabric's own load (embroidery) or from the HTML
  // <img>'s onLoad. It is read through a ref there, because the config-change
  // effect re-initializes AFTER awaiting the position switch — a continuation
  // that still holds the closures of the render it started in. Focusing an
  // embroidered product changes the config first and flips this flag one
  // render later, inside that await: the continuation then decided from the
  // old value, waited for an <img> the current render no longer has,
  // superseded the correct run, and the editor sat on its spinner for good.
  const shadingInCanvasNowRef = useRef(shadingInCanvas);
  shadingInCanvasNowRef.current = shadingInCanvas;

  /** One HTML layer of the orderable stack (base shading, heathered, texture) */
  const renderStackLayer = (layer: string, zIndex?: number) => {
    const className =
      'absolute inset-0 w-full h-full object-contain pointer-events-none';
    if (layer === 'base') {
      if (!mockupImageUrl) return null;
      return (
        <img
          key="base"
          ref={baseImageRef}
          src={mockupImageUrl}
          alt=""
          data-layer="base"
          className={className}
          style={{ opacity: baseOpacity, zIndex }}
          onLoad={() => setMockupLoaded(true)}
          onError={() => setMockupLoaded(true)}
        />
      );
    }
    if (layer === 'heathered') {
      if (!resolvedHeatheredUrl) return null;
      return (
        <img
          key="heathered"
          src={resolvedHeatheredUrl}
          alt=""
          data-layer="heathered"
          className={className}
          style={{
            mixBlendMode: mapBlendMode(
              heatheredBlendMode
            ) as React.CSSProperties['mixBlendMode'],
            opacity: heatheredOpacity,
            zIndex,
          }}
        />
      );
    }
    if (layer === 'texture') {
      if (!textureOverlayUrl) return null;
      return (
        <img
          key="texture"
          src={textureOverlayUrl}
          alt=""
          data-layer="texture"
          className={className}
          style={{
            mixBlendMode: mapBlendMode(
              textureBlendMode
            ) as React.CSSProperties['mixBlendMode'],
            opacity: textureOpacity,
            zIndex,
          }}
        />
      );
    }
    return null;
  };

  // Calculate print area position
  // The config carries the template's rectangle (templateRect) as it is.
  const printArea: PrintArea = useMemo(
    () => ({
      x: printAreaConfig.x,
      y: printAreaConfig.y,
      width: printAreaConfig.width,
      height: printAreaConfig.height,
    }),
    [printAreaConfig]
  );

  // Mirror of the active print area for the canvas event handlers, which are
  // registered once on mount and would otherwise keep a stale closure.
  const printAreaRef = useRef(printArea);

  // Cutout mask, converted to alpha — Safari on iOS ignores the luminance
  // mask mode (see lib/alphaMask.ts). Its product box drives fitToProduct.
  const cutoutMask = useAlphaMask(cutoutMaskUrl);
  // Drawstrings and the like, converted the same way.
  const toplayerMask = useAlphaMask(toplayerMaskUrl);
  const productBounds = useMemo(
    () =>
      fitToProduct && cutoutMask?.alphaUrl
        ? maskBoundsOf(cutoutMask.sourceUrl)
        : null,
    [fitToProduct, cutoutMask]
  );

  // Calculate scale factor for CSS transform, and how far the wrapper has to
  // move so the product's box — not the mockup image — sits in the middle.
  const { scaleFactor, fitOffset } = useMemo(() => {
    const plain = { scaleFactor: 1, fitOffset: { x: 0, y: 0 } };
    if (containerSize.width === 0 || containerSize.height === 0) return plain;

    const padding = containerPadding ?? CANVAS_SIZING.CONTAINER_PADDING;
    const availableWidth = containerSize.width - padding * 2;
    const availableHeight = containerSize.height - padding * 2;

    if (availableWidth <= 0 || availableHeight <= 0) return plain;

    const scaleX = availableWidth / mockupWidth;
    const scaleY = availableHeight / mockupHeight;

    let scale = Math.min(scaleX, scaleY);
    scale = Math.max(minScaleFactor, Math.min(maxScaleFactor, scale));

    return computeProductFit({
      plainScale: scale,
      availableWidth,
      availableHeight,
      mockupWidth,
      mockupHeight,
      bounds: productBounds,
    });
  }, [
    containerSize,
    mockupWidth,
    mockupHeight,
    containerPadding,
    maxScaleFactor,
    minScaleFactor,
    productBounds,
  ]);

  // Handle size in canvas pixels for the current display factor. Read through
  // a ref because the canvas event handlers are registered once on mount.
  const controlOptions = useMemo<CustomControlOptions>(() => {
    // Touch: the selection has to read on any garment. The theme colour
    // (a dark blue by default) disappears on a black shirt, so a dark
    // garment gets a white border.
    const borderColor =
      controlLayout === 'touch' && isDarkColor(effectiveBackgroundColor)
        ? '#FFFFFF'
        : undefined;
    return controlScreenSize === undefined || scaleFactor <= 0
      ? { layout: controlLayout, borderColor }
      : {
          size: controlScreenSize / scaleFactor,
          layout: controlLayout,
          borderColor,
        };
  }, [controlScreenSize, controlLayout, scaleFactor, effectiveBackgroundColor]);
  const controlOptionsRef = useRef(controlOptions);
  controlOptionsRef.current = controlOptions;
  const scaleFactorRef = useRef(scaleFactor);
  scaleFactorRef.current = scaleFactor;

  // ═══════════════════════════════════════════════════════════════════════════
  // CONTAINER SIZE OBSERVER
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!containerRef.current) return;

    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize((current) =>
          resolveContainerSize(
            { width: rect.width, height: rect.height },
            current
          )
        );
      }
    };

    updateSize();

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateSize);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    printAreaRef.current = printArea;
  }, [printArea]);

  const checkOutOfBounds = useCallback((canvas: FabricCanvas) => {
    // Read through the ref: the canvas event handlers are registered once
    // on mount, so a printArea captured in this closure would keep checking
    // against the position that was active back then.
    const area = printAreaRef.current;
    const objects = canvas
      .getObjects()
      .filter((obj) => !isSystemLayer(obj.name));
    let hasOOB = false;
    objects.forEach((obj) => {
      const bounds = obj.getBoundingRect();
      if (
        bounds.left < area.x ||
        bounds.top < area.y ||
        bounds.left + bounds.width > area.x + area.width ||
        bounds.top + bounds.height > area.y + area.height
      ) {
        hasOOB = true;
      }
    });
    if (hasOOB) {
      setIsOutOfBounds(true);
      if (outOfBoundsTimeoutRef.current) {
        clearTimeout(outOfBoundsTimeoutRef.current);
      }
      outOfBoundsTimeoutRef.current = setTimeout(
        () => setIsOutOfBounds(false),
        2000
      );
    }
  }, []);

  const updateHasDesign = useCallback((canvas: FabricCanvas) => {
    const userObjects = canvas
      .getObjects()
      .filter((obj) => !isSystemLayer(obj.name));
    setHasDesign(userObjects.length > 0);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // UPLOAD HANDLING
  // ═══════════════════════════════════════════════════════════════════════════

  const addImageFromFile = useCallback(
    async (file: File) => {
      if (!fabricRef.current) return;

      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(t('creator.canvas.unsupportedFormat'));
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          t('creator.canvas.fileTooLarge', { max: MAX_UPLOAD_LABEL })
        );
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      let originalBlobUrl: string | undefined;
      let croppedBlobUrl: string | undefined;

      try {
        const canvas = fabricRef.current;
        originalBlobUrl = URL.createObjectURL(file);

        // Crop image to visible bounds (removes transparent areas)
        // This avoids Fabric.js issues with cropX/cropY/width/height properties
        const cropResult = await cropImageToVisibleBounds(originalBlobUrl);
        croppedBlobUrl = cropResult.blobUrl;
        const bounds = cropResult.bounds;

        logger.debug(
          'CanvasEditor',
          `Visible bounds: ${bounds.width}×${bounds.height}${bounds.wasTrimmed ? ' (cropped)' : ''}`
        );

        // Create Fabric image from the cropped image
        // Note: Blob URLs don't need crossOrigin, but we set it anyway for consistency
        // When the image is later updated to S3 URL, crossOrigin will be needed
        const img = await FabricImage.fromURL(croppedBlobUrl, {
          crossOrigin: 'anonymous',
        });

        // Ensure crossOrigin is set on the image element
        // This is critical to prevent canvas from becoming tainted when source is updated
        if (img._element) {
          (img._element as HTMLImageElement).crossOrigin = 'anonymous';
        }
        (img as unknown as { crossOrigin?: string }).crossOrigin = 'anonymous';

        // Generate temp ID
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Scale and position
        const maxWidth = printArea.width * 0.8;
        const maxHeight = printArea.height * 0.8;
        const imgWidth = img.width || 1;
        const imgHeight = img.height || 1;
        const scale = Math.min(maxWidth / imgWidth, maxHeight / imgHeight, 1);

        img.scale(scale);
        img.set({
          left: printArea.x + (printArea.width - imgWidth * scale) / 2,
          top: printArea.y + (printArea.height - imgHeight * scale) / 2,
          name: file.name,
        });

        // Set asset properties
        setAssetProperties(img, {
          assetId: tempId,
          assetType: 'local',
          _uploadStatus: 'pending',
        });

        // Apply clipPath
        if (printAreaClipRef.current) {
          img.clipPath = printAreaClipRef.current;
        }

        // Apply custom controls
        applyCustomControls(img, controlOptionsRef.current);

        logger.debug('CanvasEditor', `Asset added with temp ID: ${tempId}`);

        // Add to canvas
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.requestRenderAll();
        saveToHistory('add-image');
        setIsUploading(false);

        // Track upload
        startUpload(tempId, croppedBlobUrl);

        // Upload to server
        uploadAsset(file, {
          onProgress: (percent) => setUploadProgress(percent),
        })
          .then(async (asset) => {
            logger.debug('CanvasEditor', `Asset uploaded: ${asset.id}`);
            setAssetProperties(img, {
              assetId: asset.id,
              assetType: 's3',
              _uploadStatus: 'completed',
            });
            completeUpload(tempId, asset.url);
            setUploadProgress(0);

            // Update image source to use the permanent CDN URL
            // CRITICAL: Replace the image element directly to ensure crossOrigin is preserved
            // Using setSrc() can cause the canvas to become tainted if crossOrigin is lost
            try {
              // Load new image with crossOrigin
              const newImg = await FabricImage.fromURL(asset.url, {
                crossOrigin: 'anonymous',
              });

              // Get the new image element (which has crossOrigin set)
              const newImgElement = newImg._element as
                | HTMLImageElement
                | undefined;

              if (newImgElement) {
                // CRITICAL: Replace the old image element with the new one
                // This ensures crossOrigin is preserved and canvas doesn't become tainted
                img._element = newImgElement;

                // Update the src property on the Fabric object
                (img as unknown as { src?: string }).src = asset.url;

                // Ensure crossOrigin is set on both element and object
                newImgElement.crossOrigin = 'anonymous';
                (img as unknown as { crossOrigin?: string }).crossOrigin =
                  'anonymous';

                // Force Fabric.js to recognize the new element
                img.setCoords();
                canvas.requestRenderAll();
              } else {
                // Fallback: Use setSrc but ensure crossOrigin is set
                logger.warn(
                  'CanvasEditor',
                  'New image element not found, using setSrc fallback'
                );
                const imgElement = img._element as HTMLImageElement | undefined;
                if (imgElement) {
                  imgElement.crossOrigin = 'anonymous';
                }
                (img as unknown as { crossOrigin?: string }).crossOrigin =
                  'anonymous';
                img.setSrc(asset.url);
                if (img._element) {
                  (img._element as HTMLImageElement).crossOrigin = 'anonymous';
                }
                canvas.requestRenderAll();
              }

              // Revoke both blob URLs to free memory
              if (originalBlobUrl) {
                URL.revokeObjectURL(originalBlobUrl);
              }
              if (croppedBlobUrl && croppedBlobUrl !== originalBlobUrl) {
                URL.revokeObjectURL(croppedBlobUrl);
              }
            } catch (updateError) {
              logger.warn(
                'CanvasEditor',
                'Failed to update image source:',
                updateError
              );
              // Continue anyway - the image will stay with the blob URL temporarily
            }

            // The object materially changed (temp_ assetId became the real
            // one). Serialization filters temp_ assets on purpose, so any
            // save listener that ran on object:added stored the canvas
            // WITHOUT this image — without this event nothing ever saves
            // the completed state until the user happens to touch it.
            canvas.fire('object:modified', { target: img });
          })
          .catch((error) => {
            logger.error('CanvasEditor', 'Asset upload failed:', error);
            failUpload(tempId, error.message || 'Upload failed');
            setAssetProperties(img, { _uploadStatus: 'failed' });
            setUploadProgress(0);
          });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const errorStack = error instanceof Error ? error.stack : undefined;
        logger.error('CanvasEditor', 'Error adding image', {
          error: errorMessage,
          stack: errorStack,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        });
        toast.error(t('creator.canvas.loadFailed', { message: errorMessage }));
        setIsUploading(false);
        setUploadProgress(0);

        // Clean up blob URLs if they were created
        try {
          if (originalBlobUrl) {
            URL.revokeObjectURL(originalBlobUrl);
          }
          if (croppedBlobUrl && croppedBlobUrl !== originalBlobUrl) {
            URL.revokeObjectURL(croppedBlobUrl);
          }
        } catch (cleanupError) {
          // Ignore cleanup errors
          logger.warn(
            'CanvasEditor',
            'Error during blob URL cleanup:',
            cleanupError
          );
        }
      }
    },
    [printArea, saveToHistory, startUpload, completeUpload, failUpload, t]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // EMBROIDERY UPLOAD (server analyses the motif, mapping happens in the modal)
  // ═══════════════════════════════════════════════════════════════════════════

  const addEmbroideryFromFile = useCallback(
    async (file: File) => {
      if (!fabricRef.current) return;

      const allowedEmbroideryTypes = [
        'image/png',
        'image/jpeg',
        'image/webp',
        'image/tiff',
      ];
      if (!allowedEmbroideryTypes.includes(file.type)) {
        toast.error(t('creator.canvas.embroideryUnsupportedFormat'));
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error(
          t('creator.canvas.fileTooLarge', { max: MAX_UPLOAD_LABEL })
        );
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);
      setEmbroideryProcessing(true);

      // The renderer needs the original pixels, and reading them back from the
      // posterized server preview would map already-quantized colors.
      const sourceObjectUrl = URL.createObjectURL(file);

      // The upload is a round trip; the seller can switch product or position
      // while it runs. Landing the motif wherever they ended up would attach it
      // to a product they never chose for it — and open the modal over that.
      const uploadedFor = {
        productId: useProductSelectionStore.getState().focusedProductId,
        positionId: useProductSelectionStore.getState().activePositionId,
      };

      try {
        const canvas = fabricRef.current;

        const result = await uploadEmbroideryImage(file, (percent) => {
          setUploadProgress(percent);
        });

        // Build the full storage URL (API returns a relative key like "embroidery/xxx.png")
        const storageBase =
          import.meta.env.VITE_STORAGE_URL ||
          'http://localhost:9004/designdeck';
        const previewUrl = result.previewUrl.startsWith('http')
          ? result.previewUrl
          : `${storageBase}/${result.previewUrl}`;

        const img = await FabricImage.fromURL(previewUrl, {
          crossOrigin: 'anonymous',
        });
        if (img._element) {
          (img._element as HTMLImageElement).crossOrigin = 'anonymous';
        }

        const maxWidth = printArea.width * 0.8;
        const maxHeight = printArea.height * 0.8;
        const imgWidth = img.width || 1;
        const imgHeight = img.height || 1;
        const scale = Math.min(maxWidth / imgWidth, maxHeight / imgHeight, 1);

        img.scale(scale);
        img.set({
          left: printArea.x + (printArea.width - imgWidth * scale) / 2,
          top: printArea.y + (printArea.height - imgHeight * scale) / 2,
          name: `embroidery_${file.name}`,
          selectable: true,
          evented: true,
        });

        if (printAreaClipRef.current) {
          img.clipPath = printAreaClipRef.current;
        }
        applyCustomControls(img, controlOptionsRef.current);

        const nowOn = useProductSelectionStore.getState();
        if (
          nowOn.focusedProductId !== uploadedFor.productId ||
          nowOn.activePositionId !== uploadedFor.positionId
        ) {
          URL.revokeObjectURL(sourceObjectUrl);
          toast.error(t('creator.canvas.productChangedDuringUpload'));
          return;
        }

        // The key travels on the object so the modal can be reopened in a later
        // session, when the blob URL is long gone.
        img.embFileKey = result.embFileKey;

        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.requestRenderAll();
        saveToHistory('add-embroidery');

        // startMapping narrows the suggestion to the ceiling on the way in:
        // one nearest thread per color can yield twelve, and against a ceiling
        // of six the modal would open locked with twelve dropdowns as the only
        // way out.
        startEmbroideryMapping({
          palette: result.palette,
          objectUrl: sourceObjectUrl,
          embFileKey: result.embFileKey,
        });
        setEmbroideryActive(true);

        logger.debug('CanvasEditor', `Embroidery added: ${file.name}`);
      } catch (error) {
        URL.revokeObjectURL(sourceObjectUrl);
        const msg = error instanceof Error ? error.message : String(error);
        logger.error('CanvasEditor', 'Embroidery upload failed:', msg);
        toast.error(t('creator.canvas.embroideryFailed', { message: msg }));
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
        setEmbroideryProcessing(false);
      }
    },
    [
      printArea,
      saveToHistory,
      startEmbroideryMapping,
      setEmbroideryActive,
      setEmbroideryProcessing,
      t,
    ]
  );

  // Unified file handler — routes to embroidery or normal flow
  const handleAddFile = useCallback(
    (file: File) => {
      if (isEmbroideryProduct) {
        // One motif per embroidery product: the thread counter covers the
        // mapped motif only, so a second image would carry colors past the
        // ceiling unseen. The toolbar already disables its buttons; this closes
        // the drag & drop path, which bypassed them.
        if (isEmbroideryActive) {
          toast.error(t('creator.canvas.embroiderySingleArtwork'));
          return;
        }
        // The active flag only flips once the upload has landed an object on
        // the canvas. A second file dropped during that round trip would land a
        // second motif — and Apply would render the second file's pixels into
        // the first object.
        if (isEmbroideryProcessing) {
          toast.error(t('creator.canvas.embroideryStillUploading'));
          return;
        }
        addEmbroideryFromFile(file);
        return;
      }
      addImageFromFile(file);
    },
    [
      isEmbroideryProduct,
      isEmbroideryActive,
      isEmbroideryProcessing,
      addEmbroideryFromFile,
      addImageFromFile,
      t,
    ]
  );

  // Drag & Drop handlers. Only a file drag is a drop: an image or link
  // dragged out of the page itself (a motif thumb, say) must not raise the
  // drop overlay. The default is still prevented so the browser never
  // navigates away on the release.
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragCarriesFiles(e)) return;
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (!dragCarriesFiles(e)) return;
      const images = Array.from(e.dataTransfer.files).filter((f) =>
        f.type.startsWith('image/')
      );
      // On an embroidery product only the first image can become the motif —
      // handing the rest to handleAddFile would race its guard, since none of
      // them has landed on the canvas yet when the next call checks.
      const accepted = isEmbroideryProduct ? images.slice(0, 1) : images;
      if (isEmbroideryProduct && images.length > accepted.length) {
        toast.error(t('creator.canvas.embroideryFirstImageOnly'));
      }
      accepted.forEach(handleAddFile);
    },
    [handleAddFile, isEmbroideryProduct, t]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.[0]) handleAddFile(e.target.files[0]);
      e.target.value = '';
    },
    [handleAddFile]
  );

  const handleDropZoneClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CANVAS INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!canvasRef.current || fabricRef.current) return;

    logger.debug(
      'CanvasEditor',
      `Initializing canvas ${mockupWidth}x${mockupHeight}`
    );

    const canvas = new FabricCanvas(canvasRef.current, {
      width: mockupWidth,
      height: mockupHeight,
      backgroundColor: 'transparent',
      preserveObjectStacking: true,
      enableRetinaScaling: true,
      renderOnAddRemove: false,
      skipOffscreen: true,
      selection: true,
      selectionColor: getThemeColorVar(
        '--color-canvas-selection',
        '#6366F1',
        0.1
      ),
      selectionBorderColor: getThemeColorVar(
        '--color-canvas-selection',
        '#6366F1'
      ),
      selectionLineWidth: 1,
    });

    fabricRef.current = canvas;
    // DEBUG: Expose canvas globally for console debugging
    (window as unknown as { _debugCanvas: typeof canvas })._debugCanvas =
      canvas;
    setCanvas(canvas);
    setCanvasInManager(canvas);
    saveToHistory('initial');

    // Touch optimizations on upper canvas
    const upperCanvas = canvasRef.current?.nextSibling as HTMLCanvasElement;
    if (upperCanvas) {
      upperCanvas.style.touchAction = CANVAS_TOUCH_SETTINGS.touchAction;
      upperCanvas.style.userSelect = CANVAS_TOUCH_SETTINGS.userSelect;
    }

    // Create ClipPath
    const clipPath = new Rect({
      left: printArea.x,
      top: printArea.y,
      width: printArea.width,
      height: printArea.height,
      absolutePositioned: true,
    });
    printAreaClipRef.current = clipPath;

    // The registering effect has empty deps: `printArea` in this closure is the
    // mount-time value. Read the live print area on every move instead.
    const livePrintArea = () => {
      const bounds = getCanvasPrintAreaBounds(canvas);
      if (bounds) return bounds;
      const clip = printAreaClipRef.current;
      return clip
        ? {
            x: clip.left ?? 0,
            y: clip.top ?? 0,
            width: clip.width ?? 0,
            height: clip.height ?? 0,
          }
        : printArea;
    };
    const guideColor = getThemeColorVar('--color-canvas-selection', '#6366F1');

    // Event handlers
    canvas.on('object:modified', () => {
      hideGuides(canvas);
      saveToHistory('modify');
      setHasUnsavedChanges(true); // Mark design as dirty for step-transition save
      checkOutOfBounds(canvas);
      updateSizeInfo(); // Update size info after modification
      onCanvasChange?.();
    });
    canvas.on('object:moving', (e) => {
      const target = e.target;
      if (!target || isSystemLayer(target.name)) return;
      // Alt disables snapping for fine positioning. Shift is Fabric's
      // selectionKey (multi-select) and Fabric's altActionKey only affects
      // scaling/skewing, so Alt is free during a drag. e.e is always the DOM
      // event here (mouse or touch); touch has no modifier and no override.
      const altHeld = Boolean((e.e as MouseEvent | undefined)?.altKey);
      const area = livePrintArea();
      if (altHeld) {
        hideGuides(canvas);
        checkOutOfBounds(canvas);
        return;
      }
      const center = target.getCenterPoint();
      const targetCenter = {
        x: area.x + area.width / 2,
        y: area.y + area.height / 2,
      };
      // Touch: a finger cannot hold a 6 px window, and the editor is drawn
      // scaled down on a phone — the threshold is given in screen pixels.
      const options = controlOptionsRef.current;
      const threshold =
        options.layout === 'touch' && scaleFactorRef.current > 0
          ? TOUCH_SNAP_SCREEN_PX / scaleFactorRef.current
          : SNAP_THRESHOLD_PX;
      const snap = computeCenterSnap(center, targetCenter, threshold);
      if (snap.x !== null || snap.y !== null) {
        // Fabric's dragHandler recomputes left/top from the pointer on every move
        // and fires object:moving afterwards, so this correction never accumulates.
        target.setPositionByOrigin(
          new Point(snap.x ?? center.x, snap.y ?? center.y),
          'center',
          'center'
        );
        target.setCoords();
      }
      showGuides(
        canvas,
        area,
        { v: snap.x !== null, h: snap.y !== null },
        guideColor
      );
      checkOutOfBounds(canvas);
    });
    canvas.on('mouse:up', () => hideGuides(canvas));
    canvas.on('object:scaling', () => {
      checkOutOfBounds(canvas);
      updateSizeInfo(); // Real-time size update during scaling
    });
    // Objects a restore adds or removes are the stored design coming back,
    // not a change: a design load holds the switch lock, a mount restore
    // registers itself as pendingRestore. Marking those dirty made every
    // opened design autosave, which rewrote its crops and print context
    // without anyone having touched it. Undo and redo (isRestoring) are
    // changes and stay dirty.
    const isRestoringStoredObjects = (): boolean => {
      const csm = useCanvasStateManager.getState();
      return csm.isSwitchingPosition || csm.pendingRestore !== null;
    };
    canvas.on('object:added', (e) => {
      if (e.target && !isSystemLayer(e.target.name)) {
        applyCustomControls(
          e.target as FabricObject,
          controlOptionsRef.current
        );
        if (printAreaClipRef.current) {
          e.target.clipPath = printAreaClipRef.current;
        }
        if (!isRestoringStoredObjects()) setHasUnsavedChanges(true);
      }
      canvas.requestRenderAll();
      updateHasDesign(canvas);
      onCanvasChange?.();
    });
    canvas.on('object:removed', (e) => {
      const csm = useCanvasStateManager.getState();
      if (
        e.target &&
        !isSystemLayer(e.target.name) &&
        !csm.isSwitchingPosition &&
        !csm.isRestoring &&
        getUserObjects(canvas).length === 0
      ) {
        // The user removed the last object: an empty save is intended now.
        csm.markPositionCleared(csm.activePosition, true);
      }
      if (!isRestoringStoredObjects()) setHasUnsavedChanges(true);
      updateHasDesign(canvas);
      onCanvasChange?.();
    });
    // Rubber-band and shift-click selections produce an ActiveSelection
    // that Fabric doesn't run through applyCustomControls on its own.
    const applyControlsToSelection = () => {
      const active = canvas.getActiveObject();
      if (active instanceof ActiveSelection) {
        applyCustomControls(active, controlOptionsRef.current);
        canvas.requestRenderAll();
      }
    };
    canvas.on('selection:created', (e) => {
      setSelectedObjects(e.selected || []);
      updateSizeInfo(); // Update size info when object is selected
      applyControlsToSelection();
    });
    canvas.on('selection:updated', (e) => {
      setSelectedObjects(e.selected || []);
      updateSizeInfo(); // Update size info when selection changes
      applyControlsToSelection();
    });
    canvas.on('selection:cleared', () => {
      setSelectedObjects([]);
      clearSizeInfo(); // Clear size info when nothing is selected
      hideGuides(canvas);
    });

    // A lost mouse release (alt-tab, pointer up outside the window) would
    // otherwise leave a guide on screen.
    const handleWindowBlur = () => hideGuides(canvas);
    window.addEventListener('blur', handleWindowBlur);

    // Initialize layers
    initializeLayers(canvas);

    isInitializedRef.current = true;
    previousConfigRef.current = config;
    previousPrintAreaRef.current = printArea;
    setIsInitialized(true);
    setInitialized(true);
    onCanvasReady?.(canvas);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
      if (outOfBoundsTimeoutRef.current) {
        clearTimeout(outOfBoundsTimeoutRef.current);
      }
      // NOTE: Do NOT call saveCurrentPosition() here!
      // The step transition in handleStepClick already calls saveDesign()
      // which properly saves the canvas state. Saving here would overwrite
      // the master design with single-product edit data when exiting
      // single-product mode.
      //
      // dispose() removes every object and fires object:removed for each
      // one, exactly like a user deleting it. With the handlers still
      // attached that marks the design dirty (and the position as cleared)
      // on every step change, which queued a needless follow-up autosave.
      // Detach the handlers first so the teardown is silent.
      canvas.off();
      try {
        canvas.dispose();
      } catch (error) {
        // Fabric wraps the <canvas> element in its own `.canvas-container`
        logger.warn(
          'CanvasEditor',
          'canvas.dispose() threw during unmount cleanup',
          { error }
        );
      }
      fabricRef.current = null;
      setCanvas(null);
      setCanvasInManager(null);
      setInitialized(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIG CHANGE HANDLER - Update canvas when product changes
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isInitializedRef.current) return;

    const prevConfig = previousConfigRef.current;

    // Check if mockup dimensions changed
    const dimensionsChanged =
      prevConfig.mockupWidth !== mockupWidth ||
      prevConfig.mockupHeight !== mockupHeight;

    // Check if mockup image changed
    const mockupChanged = prevConfig.mockupImageUrl !== mockupImageUrl;

    // Check if print area changed (product switch with different print area).
    // Derived against derived: both sides are canvas coordinates.
    const prevPrintArea = previousPrintAreaRef.current;
    const printAreaChanged =
      prevPrintArea === null ||
      prevPrintArea.width !== printArea.width ||
      prevPrintArea.height !== printArea.height ||
      prevPrintArea.x !== printArea.x ||
      prevPrintArea.y !== printArea.y;

    if (dimensionsChanged || mockupChanged || printAreaChanged) {
      logger.debug(
        'CanvasEditor',
        `Config changed: dimensions=${dimensionsChanged}, mockup=${mockupChanged}, printArea=${printAreaChanged}`
      );

      // If print area changed, save current state and load new state
      // This handles product switches where position slug might be same but print area differs
      if (printAreaChanged) {
        // Read fresh values from the store to avoid stale closures
        const freshStore = useProductSelectionStore.getState();
        const freshPosition = freshStore.activePosition;
        const freshPrintContext = freshStore.activePrintContext;
        const freshPositionId = freshStore.activePositionId;
        // No calibrated template is active: there is no area to switch to.
        if (!freshPrintContext) return;

        logger.debug(
          'CanvasEditor',
          'Print area changed, triggering position switch',
          {
            freshPosition,
            freshPositionId,
            freshPrintContext: {
              widthPx: freshPrintContext.widthPx,
              heightPx: freshPrintContext.heightPx,
              offsetX: freshPrintContext.offsetX,
              offsetY: freshPrintContext.offsetY,
            },
            closurePosition: activePosition,
            closurePrintContext: activePrintContext,
          }
        );

        // The new product's overlays must not sit on the old canvas while the
        // switch is in flight; reinitializeLayers flips this back.
        setMockupLoaded(false);
        switchPosition(freshPosition, freshPrintContext, freshPositionId).then(
          (switched) => {
            if (!switched) {
              // A design load holds the switch lock - this effect exists to
              // race exactly that (see the lock comment in
              // canvasStateManager.loadFromDesign). Applying the new
              // dimensions, clipPath and layers now would dress the canvas
              // for a position the store never switched to. Leaving
              // previousConfigRef behind makes the effect retry once the
              // lock releases (isSwitchingPosition is a dependency).
              logger.debug(
                'CanvasEditor',
                'Config-driven switch refused, waiting for the lock'
              );
              return;
            }

            previousConfigRef.current = config;
            previousPrintAreaRef.current = printArea;

            // Update canvas dimensions after state is loaded
            if (dimensionsChanged) {
              canvas.setDimensions({
                width: mockupWidth,
                height: mockupHeight,
              });
            }

            // Update ClipPath
            const newClipPath = new Rect({
              left: printArea.x,
              top: printArea.y,
              width: printArea.width,
              height: printArea.height,
              absolutePositioned: true,
            });
            printAreaClipRef.current = newClipPath;

            // Re-apply clipPath to user objects
            const userObjects = canvas
              .getObjects()
              .filter((obj) => !isSystemLayer(obj.name));
            userObjects.forEach((obj) => {
              obj.clipPath = newClipPath;
              applyCustomControls(obj, controlOptionsRef.current);
            });

            // Re-initialize layers
            reinitializeLayers(canvas);
          }
        );
      } else {
        // No print area change, just update dimensions and layers
        // Update canvas dimensions
        if (dimensionsChanged) {
          canvas.setDimensions({ width: mockupWidth, height: mockupHeight });
        }

        // Update ClipPath
        const newClipPath = new Rect({
          left: printArea.x,
          top: printArea.y,
          width: printArea.width,
          height: printArea.height,
          absolutePositioned: true,
        });
        printAreaClipRef.current = newClipPath;

        // Re-initialize layers
        reinitializeLayers(canvas);
        previousConfigRef.current = config;
        previousPrintAreaRef.current = printArea;
      }
    } else {
      // The derived print area moves with the config, so both refs advance
      // together — and only here, never after a refused switch, which the
      // effect retries once the lock releases.
      previousConfigRef.current = config;
      previousPrintAreaRef.current = printArea;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config,
    isSwitchingPosition,
    mockupWidth,
    mockupHeight,
    mockupImageUrl,
    printArea,
    switchPosition,
    // NOTE: Removed activePosition, activePrintContext, activePositionId from dependencies
    // to prevent duplicate switchPosition calls. Position switching is handled by
    // the dedicated position change useEffect (lines 817-899).
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // LAYER INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════════════

  const initializeLayers = useCallback(
    async (canvas: FabricCanvas): Promise<boolean> => {
      const isStale = layerGuardRef.current.next();
      logger.debug('CanvasEditor', 'Initializing layers...');

      // Background — only paint a color when a cutout mask exists to clip it to
      // the product silhouette; otherwise it would bleed across the full canvas.
      // Transparent when an overlay is rendered under the canvas (see
      // canvasBackgroundFill); the wrapper carries the colour in that case.
      const bgRect = new Rect({
        left: 0,
        top: 0,
        width: mockupWidth,
        height: mockupHeight,
        fill: canvasBackgroundFill,
        selectable: false,
        evented: false,
        excludeFromExport: false,
        name: SYSTEM_LAYER_NAMES.BACKGROUND,
      });
      canvas.add(bgRect);

      // Mockup image (the shading map). Loaded into Fabric only when it sits
      // below the design (embroidery). Above the design it is an HTML overlay
      // (see JSX) that reports readiness through its own onLoad, so the image
      // is fetched exactly once either way.
      const shadingInCanvasNow = shadingInCanvasNowRef.current;
      if (mockupImageUrl && shadingInCanvasNow) {
        try {
          const mockupImage = await FabricImage.fromURL(mockupImageUrl, {
            crossOrigin: 'anonymous',
          });
          if (isStale()) {
            // A newer configuration re-initialized the layers meanwhile;
            // adding this mockup/border would paint the previous product.
            logger.debug('CanvasEditor', 'Discarding stale layer init');
            return false;
          }
          removeSystemLayer(canvas, SYSTEM_LAYER_NAMES.MOCKUP);
          if (shadingInCanvasNowRef.current) {
            mockupImage.set({
              left: 0,
              top: 0,
              opacity: baseOpacity,
              selectable: false,
              evented: false,
              excludeFromExport: false,
              name: SYSTEM_LAYER_NAMES.MOCKUP,
            });

            // Scale to fit canvas
            const scaleX = mockupWidth / (mockupImage.width || mockupWidth);
            const scaleY = mockupHeight / (mockupImage.height || mockupHeight);
            mockupImage.scale(Math.min(scaleX, scaleY));

            canvas.add(mockupImage);
          }
          setMockupLoaded(true);
          logger.debug('CanvasEditor', 'Mockup image loaded', {
            shadingInCanvas: shadingInCanvasNow,
          });
        } catch (error) {
          logger.error('CanvasEditor', 'Failed to load mockup image:', error);
          if (isStale()) return false;
          setMockupLoaded(true);
        }
      } else if (mockupImageUrl) {
        // A re-init that keeps the same base URL (colour, dimensions, print
        // area) never fires onLoad again: take the browser's word for it.
        if (baseImageRef.current?.complete) setMockupLoaded(true);
      } else {
        setMockupLoaded(true);
      }

      // Print area border. In Fabric the stroke is part of an object's box:
      // a rect at (x, y) with `width` paints from x to x + width + strokeWidth.
      // Sized to the full print area it therefore ran one stroke past the
      // right and bottom edges — harmless on a mockup with margin, but when
      // the canvas IS the print area (guided frame) those two edges were
      // clipped away. Shrinking by the whole stroke keeps the box inside.
      const borderStroke = 2;
      removeSystemLayer(canvas, SYSTEM_LAYER_NAMES.PRINT_AREA_BORDER);
      const printAreaBorder = new Rect({
        left: printArea.x,
        top: printArea.y,
        width: printArea.width - borderStroke,
        height: printArea.height - borderStroke,
        fill: 'transparent',
        stroke: '#818cf8',
        strokeDashArray: [6, 4],
        strokeWidth: borderStroke,
        selectable: false,
        evented: false,
        excludeFromExport: true,
        name: SYSTEM_LAYER_NAMES.PRINT_AREA_BORDER,
      });
      if (drawPrintAreaBorder) canvas.add(printAreaBorder);

      canvas.sendObjectToBack(bgRect);
      canvas.requestRenderAll();
      return true;
    },
    [
      mockupWidth,
      mockupHeight,
      canvasBackgroundFill,
      drawPrintAreaBorder,
      mockupImageUrl,
      baseOpacity,
      printArea,
    ]
  );

  const reinitializeLayers = useCallback(
    async (canvas: FabricCanvas) => {
      logger.debug('CanvasEditor', 'Reinitializing layers...');
      hideGuides(canvas);
      setMockupLoaded(false);

      // Remove system layers using the service (more robust)
      removeSystemLayers(canvas);

      // Re-add layers
      const completed = await initializeLayers(canvas);
      if (!completed) {
        // A newer configuration started meanwhile; that run owns the
        // z-order fixup and clipPath re-application below.
        return;
      }

      // Fix z-order: initializeLayers adds system layers on top of existing
      // user objects. We need: background → mockup → user objects → print area border.
      const allObjects = canvas.getObjects();
      const printAreaBorder = allObjects.find(
        (obj) => obj.name === SYSTEM_LAYER_NAMES.PRINT_AREA_BORDER
      );

      // Bring all user objects above system layers (mockup)
      allObjects.forEach((obj) => {
        if (!isSystemLayer(obj.name)) {
          canvas.bringObjectToFront(obj);
        }
      });

      // Ensure print area border stays on top of everything
      if (printAreaBorder) {
        canvas.bringObjectToFront(printAreaBorder);
      }

      // Re-apply clipPath to user objects using the service
      applyClipPathToUserObjects(canvas, printArea);

      canvas.requestRenderAll();
    },
    [initializeLayers, printArea]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // POSITION SWITCHING - Now delegated to CanvasStateManager
  // ═══════════════════════════════════════════════════════════════════════════

  const previousActivePositionRef = useRef<string>(activePosition);
  // Target of the switch currently in flight. Replaces the old synchronous
  // ref advance as the re-entry guard: the ref now moves only once the store
  // confirms the switch ran (analysis 2026-09-02-004, finding L).
  const switchInFlightRef = useRef<string | null>(null);

  // Keep CanvasStateManager in sync with ProductSelectionStore's active
  // context for the SAME position: initial mount, a product switch, and the
  // focused product being applied after a design (re)load. Cross-position
  // switches are handled by the effect below. switchPosition() is idempotent
  // when nothing changed and skipped while a design load holds the lock.
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (!activePositionId || !activePrintContext) return;
    if (activePosition !== previousActivePositionRef.current) return;

    switchPosition(activePosition, activePrintContext, activePositionId);
    logger.debug(
      'CanvasEditor',
      `Synced active context: ${activePosition} → ${activePositionId}`
    );
  }, [
    isInitialized,
    activePosition,
    activePrintContext,
    activePositionId,
    switchPosition,
  ]);
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isInitializedRef.current) return;

    const prevPosition = previousActivePositionRef.current;
    if (prevPosition === activePosition) return;
    if (switchInFlightRef.current === activePosition) return;
    if (!activePrintContext) return;

    logger.debug(
      'CanvasEditor',
      `Position switch: ${prevPosition} → ${activePosition} (positionId: ${activePositionId})`
    );

    // Debug: Position switch in CanvasEditor
    logger.debug('CanvasEditor', 'Position switch triggered', {
      from: prevPosition,
      to: activePosition,
      activePositionId,
      activePrintContext: {
        offsetX: activePrintContext.offsetX,
        offsetY: activePrintContext.offsetY,
        widthPx: activePrintContext.widthPx,
        heightPx: activePrintContext.heightPx,
      },
      printAreaClipRef: printAreaClipRef.current
        ? {
            left: printAreaClipRef.current.left,
            top: printAreaClipRef.current.top,
            width: printAreaClipRef.current.width,
            height: printAreaClipRef.current.height,
          }
        : null,
    });

    // Delegate to CanvasStateManager - handles save, load, and coordinate transformation
    switchInFlightRef.current = activePosition;
    switchPosition(activePosition, activePrintContext, activePositionId)
      .then((switched) => {
        switchInFlightRef.current = null;

        if (!switched) {
          // The store refused: a design load or another switch holds the lock.
          // previousActivePositionRef stays behind, so this effect retries as
          // soon as isSwitchingPosition flips back to false.
          logger.debug(
            'CanvasEditor',
            `Position switch refused, waiting for the lock: ${prevPosition} → ${activePosition}`
          );
          return;
        }

        previousActivePositionRef.current = activePosition;

        // Re-apply clipPath and controls to loaded objects
        const userObjects = canvas
          .getObjects()
          .filter((obj) => !isSystemLayer(obj.name));

        logger.debug(
          'CanvasEditor',
          'After switchPosition, applying controls',
          {
            userObjectCount: userObjects.length,
            objects: userObjects.map((obj, i) => ({
              index: i,
              type: obj.type,
              name: obj.name,
              left: obj.left,
              top: obj.top,
              controlsBefore: obj.controls
                ? Object.keys(obj.controls)
                : 'undefined',
            })),
          }
        );

        canvas.getObjects().forEach((obj) => {
          if (!isSystemLayer(obj.name)) {
            if (printAreaClipRef.current) {
              obj.clipPath = printAreaClipRef.current;
            }
            applyCustomControls(obj, controlOptionsRef.current);
          }
        });

        // Log after applying controls
        canvas.requestRenderAll();
        updateHasDesign(canvas);
      })
      .catch((error) => {
        // Parts of switchPosition run outside its try/catch, so it can reject.
        // Without this the in-flight guard stays pinned to the target and
        // blocks every later switch to that position for the rest of the mount.
        switchInFlightRef.current = null;
        logger.error('CanvasEditor', 'Position switch failed:', error);
      });
  }, [
    activePosition,
    activePrintContext,
    activePositionId,
    isSwitchingPosition,
    switchPosition,
    updateHasDesign,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // FINALIZE RESTORED OBJECTS
  // ═══════════════════════════════════════════════════════════════════════════
  // Objects restored by any path (remount restore, loadFromDesign) need
  // controls, clipPath, z-order and a fresh preview. Extracted so the
  // switch-lock effect below can run it as well.

  const finalizeRestoredObjects = useCallback(
    (canvas: FabricCanvas) => {
      // Re-apply clipPath and controls to loaded objects
      // Also ensure proper z-order: user objects should be above mockup but below print area border
      const objects = canvas.getObjects();

      // Nothing was restored: bail out before updatePreview would extract an
      // empty canvas and overwrite the master's relativeState/crop with
      // null. The lock-release effect also fires for plain switchPosition
      // cycles, where an empty canvas is a legitimate state, not a deletion.
      // The guard belongs here and not in updatePreview - usePositionPreviews
      // relies on updatePreview clearing when the user deletes the last object.
      const userObjects = objects.filter((obj) => !isSystemLayer(obj.name));
      if (userObjects.length === 0) return;

      const printAreaBorder = objects.find(
        (obj) => obj.name === SYSTEM_LAYER_NAMES.PRINT_AREA_BORDER
      );

      objects.forEach((obj) => {
        if (!isSystemLayer(obj.name)) {
          if (printAreaClipRef.current) {
            obj.clipPath = printAreaClipRef.current;
          }
          applyCustomControls(obj, controlOptionsRef.current);

          // Ensure user object is below print area border
          if (printAreaBorder) {
            canvas.bringObjectToFront(obj);
          }
        }
      });

      // Ensure print area border is always on top
      if (printAreaBorder) {
        canvas.bringObjectToFront(printAreaBorder);
      }

      canvas.requestRenderAll();
      updateHasDesign(canvas);

      // Regenerate preview for ProductCards after restoring objects.
      // loadFromDesign sets preview: null (canvas was unmounted),
      // so we must explicitly regenerate here.
      updatePreview(activePosition);
    },
    [activePosition, updateHasDesign, updatePreview]
  );

  // The handle size follows the display factor (see controlOptions); objects
  // already on the canvas carry the size they were configured with, so they
  // are refreshed whenever the factor moves — a container resize, for one.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isInitializedRef.current || controlScreenSize === undefined)
      return;
    let touched = false;
    canvas.getObjects().forEach((obj) => {
      if (isSystemLayer(obj.name)) return;
      applyCustomControls(obj, controlOptions);
      touched = true;
    });
    if (touched) canvas.requestRenderAll();
  }, [controlOptions, controlScreenSize, isInitialized]);

  // A design load restores objects while the switch lock is held and the
  // remount restore bails out; finalize once the lock releases
  // (isSwitchingPosition is subscribed with the other stores above).
  const wasSwitchingRef = useRef(false);

  useEffect(() => {
    const canvas = fabricRef.current;
    if (isSwitchingPosition) {
      wasSwitchingRef.current = true;
      return;
    }
    if (!wasSwitchingRef.current) return;

    // CRITICAL: Must wait for mockupLoaded, same as the remount restore below.
    // Finalizing needs the print area border (z-order) and printAreaClipRef
    // (clipPath); both only exist once the canvas is initialized and the mockup
    // layers are in place. The ref stays set until we actually finalized, so
    // this effect retries when initialization or the mockup arrives - the
    // remount restore cannot cover for us here, because loadSavedPosition
    // returns false once the canvas already holds the restored objects.
    if (!canvas || !isInitializedRef.current || !mockupLoaded) return;

    wasSwitchingRef.current = false;
    finalizeRestoredObjects(canvas);
  }, [
    isSwitchingPosition,
    isInitialized,
    mockupLoaded,
    finalizeRestoredObjects,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // LOAD SAVED POSITION ON REMOUNT
  // ═══════════════════════════════════════════════════════════════════════════
  // When CanvasEditor remounts (e.g., returning from Step 2 to Step 1),
  // the position-switch effect won't trigger because prevPosition === activePosition.
  // This effect loads any previously saved design data for the active position.
  // CRITICAL: Must wait for mockupLoaded to ensure proper layer ordering.

  const hasLoadedSavedPositionRef = useRef(false);

  useEffect(() => {
    const canvas = fabricRef.current;
    // Wait for both initialization AND mockup to be loaded
    if (!canvas || !isInitializedRef.current || !mockupLoaded) return;

    // Only run once per mount, after initialization
    if (hasLoadedSavedPositionRef.current) return;
    hasLoadedSavedPositionRef.current = true;

    // Check if there's saved data for the active position
    const positionState =
      useCanvasStateManager.getState().masterPositions[activePosition];
    if (positionState?.json?.objects && positionState.json.objects.length > 0) {
      // Prefer stored printContext from the position itself over the
      // productSelectionStore value, which may still be null or stale
      // after a loadDesign call that hasn't fully synced yet.
      const effectivePrintContext =
        positionState.printContext ?? activePrintContext;

      logger.debug(
        'CanvasEditor',
        'Remount detected with saved data, loading position',
        {
          position: activePosition,
          objectCount: positionState.json.objects.length,
        }
      );

      loadSavedPosition(activePosition, effectivePrintContext).then(
        (loaded) => {
          if (loaded) {
            finalizeRestoredObjects(canvas);
          }
        }
      );
    }
  }, [
    isInitialized,
    mockupLoaded, // Added: wait for mockup to be loaded
    activePosition,
    activePrintContext,
    loadSavedPosition,
    finalizeRestoredObjects,
  ]);

  // The embroidery flag (useEmbroideryStore) is set by ProductPreview in an
  // effect, i.e. one render after the config that triggered the last layer
  // init. When it flips the base has to move between Fabric (below the
  // design) and the HTML overlay (above it), so the layers are rebuilt.
  const shadingInCanvasRef = useRef(shadingInCanvas);
  useEffect(() => {
    if (shadingInCanvasRef.current === shadingInCanvas) return;
    shadingInCanvasRef.current = shadingInCanvas;
    const canvas = fabricRef.current;
    if (!canvas || !isInitialized) return;
    void reinitializeLayers(canvas);
  }, [shadingInCanvas, isInitialized, reinitializeLayers]);

  // ═══════════════════════════════════════════════════════════════════════════
  // UPDATE BACKGROUND COLOR
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!fabricRef.current || !isInitialized) return;

    const canvas = fabricRef.current;
    const bgRect = canvas
      .getObjects()
      .find((obj) => obj.name === SYSTEM_LAYER_NAMES.BACKGROUND) as Rect;

    if (bgRect) {
      bgRect.set({ fill: canvasBackgroundFill });
      canvas.requestRenderAll();
    }
  }, [canvasBackgroundFill, isInitialized]);

  // ═══════════════════════════════════════════════════════════════════════════
  // PAN MODE — toggle cursor/selection when toolMode switches to 'pan'
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isInitialized) return;

    if (toolMode === 'pan') {
      canvas.defaultCursor = 'grab';
      canvas.hoverCursor = 'grab';
      canvas.selection = false;
      canvas.discardActiveObject();
      canvas.getObjects().forEach((obj) => {
        if (!isSystemLayer(obj.name)) {
          obj.set({ selectable: false, evented: false });
        }
      });
    } else {
      canvas.defaultCursor = 'default';
      canvas.hoverCursor = 'move';
      canvas.selection = true;
      canvas.getObjects().forEach((obj) => {
        if (!isSystemLayer(obj.name)) {
          obj.set({ selectable: true, evented: true });
        }
      });
    }
    canvas.requestRenderAll();
  }, [toolMode, isInitialized]);

  // ═══════════════════════════════════════════════════════════════════════════
  // PAN MODE — mouse event handlers for viewport panning
  // Uses native DOM events (same pattern as touch settings in init effect)
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas || !isInitialized) return;

    // Upper canvas is Fabric's interactive layer — next sibling of lower canvas
    const upperCanvas = canvasRef.current
      ?.nextSibling as HTMLCanvasElement | null;
    if (!upperCanvas) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (toolMode !== 'pan') return;
      isPanningRef.current = true;
      upperCanvas.style.cursor = 'grabbing';
      lastPanPosRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isPanningRef.current) return;
      const dx = (e.clientX - lastPanPosRef.current.x) / scaleFactor;
      const dy = (e.clientY - lastPanPosRef.current.y) / scaleFactor;
      canvas.relativePan(new Point(dx, dy));
      lastPanPosRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      if (!isPanningRef.current) return;
      isPanningRef.current = false;
      if (toolMode === 'pan') upperCanvas.style.cursor = 'grab';
    };

    // mousedown on the canvas, move/up on window so pan continues if mouse leaves canvas
    upperCanvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      upperCanvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [toolMode, scaleFactor, isInitialized]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  // Wrapper style - contains the native-sized canvas, scaled with transform.
  // The wrapper is hidden while its mask is converted, and the loading state
  // inside it with it — so that one is rendered outside the wrapper then.
  const maskPending = mockupLoaded && isMaskPending(cutoutMask);
  const wrapperStyle: React.CSSProperties = useMemo(
    () => ({
      width: mockupWidth,
      height: mockupHeight,
      transform: `translate(${fitOffset.x}px, ${fitOffset.y}px) scale(${scaleFactor})`,
      transformOrigin: 'center center',
      ...(cutoutMask && mockupLoaded ? cutoutMaskStyles(cutoutMask) : {}),
    }),
    [
      mockupWidth,
      mockupHeight,
      scaleFactor,
      fitOffset,
      cutoutMask,
      mockupLoaded,
    ]
  );

  // The top overlay is a sibling of the (masked) wrapper, laid out exactly
  // like it: same native size and scale, centred in the same flex parent,
  // but without the cutout mask so frames and clips are never clipped.
  const topOverlayStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'absolute',
      left: '50%',
      top: '50%',
      width: mockupWidth,
      height: mockupHeight,
      transform: `translate(calc(-50% + ${fitOffset.x}px), calc(-50% + ${fitOffset.y}px)) scale(${scaleFactor})`,
      transformOrigin: 'center center',
      zIndex: 6,
    }),
    [mockupWidth, mockupHeight, scaleFactor, fitOffset]
  );

  // Everything that happens to embroidery artwork once it is on the canvas —
  // apply, remove, and keeping the "there is artwork here" flag honest — lives
  // in its own hook so the confirm sequence can be tested on its own.
  const embroidery = useEmbroideryMapping({
    fabricRef,
    activePrintContext,
    saveToHistory,
    isInitialized,
  });
  return (
    <div
      ref={containerRef}
      className={cn(
        'flex-1 flex items-center justify-center relative',
        className
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        onChange={handleFileInputChange}
        className="hidden"
      />

      {/* Canvas wrapper - native size, scaled with transform */}
      <div
        ref={canvasWrapperRef}
        className="canvas-editor-wrapper relative overflow-visible"
        style={{
          ...wrapperStyle,
          ...(cutoutMaskUrl
            ? { backgroundColor: effectiveBackgroundColor }
            : {}),
        }}
      >
        {/* Layers under the design that are not drawn by Fabric: painted
            behind the (then transparent) canvas background in DOM order */}
        {mockupLoaded &&
          overlaysBelowCanvas.map((layer) => renderStackLayer(layer))}

        {/* Fabric.js Canvas. Fabric moves the <canvas> into a
            `.canvas-container` it creates, so the element gets a host of its
            own: as a direct child it would stay React's insertBefore anchor
            for the overlays above and throw once one of them mounts. The
            host renders as `display: contents` (same idiom as
            MockupRenderer) so it adds no box between wrapper and canvas. */}
        <div className="contents">
          <canvas ref={canvasRef} />
        </div>

        {/* Layers above the design (shading base, heathered, texture per the
            template order) as overlays on top of the canvas */}
        {layersAboveDesign.map((layer, index) =>
          layer === 'base' || mockupLoaded
            ? renderStackLayer(layer, 2 + index)
            : null
        )}

        {/* Shadow Overlay — pinned above the stack, below the cutout mask */}
        {shadowOverlayUrl && mockupLoaded && (
          <img
            src={shadowOverlayUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-contain pointer-events-none z-[5]"
            style={{
              mixBlendMode: mapBlendMode(
                shadowBlendMode
              ) as React.CSSProperties['mixBlendMode'],
              opacity: shadowOpacity,
            }}
          />
        )}

        {/* Drawstrings and the like — garment parts that hang in front of
            the print. Built the way the garment itself is (product colour
            with the shading map over it), clipped to the cord mask and drawn
            above the whole stack, so a design cannot run across them. The
            shading map alone would not do: there the cords carry the same
            alpha as the fabric around them. Same construction as in
            MockupRenderer and the print render (layer-compositor).
            Drawn only once the mask is converted to alpha: with the
            luminance fallback iOS would ignore the mask and paint a full
            square in the product colour over the whole design. A missing
            cord is harmless, a covered design is not — so a pending or
            failed conversion draws nothing. */}
        {toplayerMask?.alphaUrl && mockupImageUrl && mockupLoaded && (
          <div
            data-layer="toplayer"
            className="absolute inset-0 pointer-events-none z-[6]"
            style={{
              ...cutoutMaskStyles(toplayerMask),
              backgroundColor: effectiveBackgroundColor,
            }}
          >
            <img
              src={mockupImageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-contain"
              style={{ opacity: baseOpacity }}
            />
          </div>
        )}

        {/* Loading state */}
        {!mockupLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-[var(--paper-2)] animate-pulse z-[1]">
            <Loader2 className="w-8 h-8 text-[var(--mute)] animate-spin" />
          </div>
        )}

        {/* Drop zone (when no design) */}
        {!hasDesign && mockupLoaded && (
          <div
            className={cn(
              // `canvas-drop-zone` is a hook for embedders: Direct Sale's
              // phone layout hides the zone, its bottom card is the way in.
              'canvas-drop-zone absolute z-[5] cursor-pointer transition-all duration-200',
              'flex flex-col items-center justify-center gap-3',
              // No border here on purpose: the print area frame is drawn once,
              // as the __printAreaBorder__ rect on the Fabric canvas. A border
              // on this overlay sits on the same rect but inside the border box,
              // so it renders as a second, offset frame on every empty position.
              isDragOver && 'bg-[var(--cobalt-100)]/20'
            )}
            style={{
              left: printArea.x,
              top: printArea.y,
              width: printArea.width,
              height: printArea.height,
            }}
            onClick={handleDropZoneClick}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-8 h-8 text-[var(--cobalt-300)] animate-spin" />
                <span className="text-sm text-[var(--cobalt-100)]">
                  {isEmbroideryProduct
                    ? t('creator.canvas.digitizing')
                    : `${uploadProgress}%`}
                </span>
              </>
            ) : (
              <>
                <div className="w-[46px] h-[46px] flex items-center justify-center bg-[var(--primary-soft)] rounded-full">
                  <Plus className="w-5 h-5 text-[var(--cobalt-500)]" />
                </div>
                <span className="font-display font-semibold text-base tracking-[-0.01em] text-[var(--cobalt-100)] text-center px-2">
                  {isDragOver
                    ? t('creator.canvas.dropHere')
                    : isEmbroideryProduct
                      ? t('creator.canvas.uploadEmbroidery')
                      : t('creator.canvas.uploadDesign')}
                </span>
                <span className="text-xs text-[var(--cobalt-300)] -mt-1.5 text-center px-2">
                  {isEmbroideryProduct
                    ? t('creator.canvas.embroideryFormats')
                    : t('creator.canvas.dragOrClick')}
                </span>
              </>
            )}
          </div>
        )}

        {/* Drag overlay */}
        {isDragOver && hasDesign && (
          <div className="absolute inset-0 z-[10] bg-primary-100/70 border-dashed border-primary rounded-control flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-primary" />
              <span className="text-primary font-medium">
                {t('creator.canvas.dropToAdd')}
              </span>
            </div>
          </div>
        )}
      </div>

      <EmbroideryThreadModal
        isOpen={embroidery.isModalOpen}
        sourceUrl={embroidery.sourceUrl}
        onCancel={embroidery.cancel}
        onConfirm={embroidery.confirm}
        onRemoveMotif={embroidery.removeArtwork}
      />

      {/* Mockup loaded, mask still converting: the wrapper is invisible, yet
          its canvas would take pointer input. Cover it and show the wait. */}
      {maskPending && (
        <div
          data-testid="canvas-mask-pending"
          className="absolute inset-0 z-[6] flex items-center justify-center"
        >
          <Loader2 className="w-8 h-8 text-[var(--mute)] animate-spin" />
        </div>
      )}

      {/* Overlay — above the whole stack AND above the cutout mask. Rendered
          as a sibling of the masked wrapper so it is never clipped. */}
      {topOverlayUrl && mockupLoaded && (
        <img
          src={topOverlayUrl}
          alt=""
          data-layer="overlay"
          className="object-contain pointer-events-none"
          style={topOverlayStyle}
        />
      )}

      {/* Out of bounds warning - positioned outside scaled wrapper */}
      {isOutOfBounds && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] border border-warning rounded-control ring-1 ring-line">
          <AlertTriangle className="w-4 h-4 text-warning" />
          <span className="text-sm text-warning">
            {t('creator.canvas.outOfBounds')}
          </span>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

export function getUserDesigns(canvas: FabricCanvas): FabricObject[] {
  return canvas.getObjects().filter((obj) => !isSystemLayer(obj.name));
}

export function addDesignToCanvas(
  canvas: FabricCanvas,
  object: FabricObject,
  printArea: PrintArea,
  clipPath: Rect | null
): void {
  if (clipPath) {
    object.clipPath = clipPath;
  }
  const objWidth = (object.width || 0) * (object.scaleX || 1);
  const objHeight = (object.height || 0) * (object.scaleY || 1);
  object.set({
    left: printArea.x + (printArea.width - objWidth) / 2,
    top: printArea.y + (printArea.height - objHeight) / 2,
  });
  canvas.add(object);
  canvas.setActiveObject(object);
  canvas.requestRenderAll();
}

export function isOutOfPrintArea(
  object: FabricObject,
  printArea: PrintArea
): boolean {
  const bounds = object.getBoundingRect();
  return (
    bounds.left < printArea.x ||
    bounds.top < printArea.y ||
    bounds.left + bounds.width > printArea.x + printArea.width ||
    bounds.top + bounds.height > printArea.y + printArea.height
  );
}

export default CanvasEditor;
